import { cloudSave, cloudStatus, type CloudLoadResult } from './directory';
import { getOrCreateUserId } from './coop.svelte';
import { migrateLegacyStorageKeys } from './migrate-storage';

export const CLOUD_STORAGE_KEYS = [
	'bubble-games-progress',
	'bubble-games-state',
	'bubble-games-completions',
	'bubble-games-settings',
	'bubble-games-active-coop',
	'coop-user-id',
	// Per-device auth token; restored with the userId so the new device can
	// authenticate against the server's already-stored token hash.
	'coop-user-token'
];

const PIN_KEY = 'bubble-games-cloud-pin';
const LAST_SYNC_KEY = 'bubble-games-cloud-last-sync';

export function getStoredPin(): string | null {
	try { return localStorage.getItem(PIN_KEY); } catch { return null; }
}

export function setStoredPin(pin: string): void {
	try { localStorage.setItem(PIN_KEY, pin); } catch {}
}

export function clearStoredPin(): void {
	try { localStorage.removeItem(PIN_KEY); } catch {}
}

export function getLastSync(): number | null {
	try {
		const raw = localStorage.getItem(LAST_SYNC_KEY);
		return raw ? parseInt(raw, 10) : null;
	} catch { return null; }
}

function setLastSync(ms: number): void {
	try { localStorage.setItem(LAST_SYNC_KEY, String(ms)); } catch {}
}

export function generatePin(): string {
	const n = Math.floor(Math.random() * 10000);
	return n.toString().padStart(4, '0');
}

export function collectSaveData(): Record<string, unknown> {
	const data: Record<string, unknown> = {};
	for (const key of CLOUD_STORAGE_KEYS) {
		const raw = localStorage.getItem(key);
		if (raw == null) continue;
		try { data[key] = JSON.parse(raw); }
		catch { data[key] = raw; }
	}
	return data;
}

export function applySaveData(data: Record<string, unknown>): void {
	for (const key of CLOUD_STORAGE_KEYS) {
		if (!(key in data)) continue;
		const v = data[key];
		const raw = typeof v === 'string' ? v : JSON.stringify(v);
		localStorage.setItem(key, raw);
	}
}

export async function syncNow(): Promise<{ ok: boolean; error?: string; syncedAt?: number }> {
	const pin = getStoredPin();
	if (!pin) return { ok: false, error: 'No PIN configured' };
	const userId = getOrCreateUserId();
	const data = collectSaveData();
	const res = await cloudSave(userId, pin, data);
	if (res.ok && res.syncedAt) setLastSync(res.syncedAt);
	return res;
}

export async function applyCloudSave(result: Extract<CloudLoadResult, { ok: true }>): Promise<void> {
	applySaveData(result.data);
	// Cloud snapshots from older builds may carry legacy GameKey strings.
	// Re-run the migration so anything we just hydrated gets normalized.
	migrateLegacyStorageKeys();
	if (result.syncedAt) setLastSync(result.syncedAt);
}

export async function getCloudStatus(): Promise<{ hasPin: boolean; syncedAt: number | null }> {
	const userId = getOrCreateUserId();
	return await cloudStatus(userId);
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Debounced auto-sync. Safe to call as often as you like. */
export function scheduleSync(delayMs = 3000): void {
	if (debounceTimer) clearTimeout(debounceTimer);
	debounceTimer = setTimeout(() => {
		debounceTimer = null;
		void syncNow();
	}, delayMs);
}
